import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDetectOutsideClick } from '../../../../../generic/hooks/useDetectOutsideClick'
import FilterIcon from '../../../../../generic/icons/IconLib/FilterIcon'
import { FormShell, FormGrid, FormField } from '../../../../../generic/ui/FormLayout'
import CustomerSelector from '../../../../../generic/ui/Selectors/CustomerSelector'
import GrowerSelector from '../../../../../generic/ui/Selectors/GrowerSelector'
import { IClientsItem } from '../../../../../modules/API/Defaults/models/customers'
import { IGrowerItem } from '../../../../../modules/API/Defaults/models/growers'

type Props = {
  customerList: IClientsItem[]
  growerList: IGrowerItem[]
  selectedCustomer: number
  setSelectedCustomer: (id: number) => void
  selectedGrower: number
  setSelectedGrower: (id: number) => void
}

const SellerFilterButton = ({
  customerList,
  growerList,
  selectedCustomer,
  setSelectedCustomer,
  selectedGrower,
  setSelectedGrower,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)

  useDetectOutsideClick(wrapperRef, () => setOpen(false))

  const customerObj = useMemo(
    () => customerList.find((c) => c.CustomerID === selectedCustomer) ?? null,
    [customerList, selectedCustomer],
  )
  const growerObj = useMemo(
    () => growerList.find((g) => g.GrowerID === selectedGrower) ?? null,
    [growerList, selectedGrower],
  )

  const activeCount = (selectedCustomer ? 1 : 0) + (selectedGrower ? 1 : 0)

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type='button'
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-label='סינון'
        $active={activeCount > 0}
        $open={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FilterIcon />
        {activeCount > 0 && <Count>{activeCount}</Count>}
      </Trigger>

      {open && (
        <Panel role='dialog'>
          <FormShell>
            <FormGrid>
              <FormField label='לקוח' fullWidth>
                <CustomerSelector
                  customers={customerList}
                  value={customerObj}
                  onSelect={(c) => setSelectedCustomer(c?.CustomerID ?? 0)}
                  allowClear
                />
              </FormField>
              <FormField label='מגדל' fullWidth>
                <GrowerSelector
                  growers={growerList}
                  value={growerObj}
                  onSelect={(g) => setSelectedGrower(g?.GrowerID ?? 0)}
                  allowClear
                />
              </FormField>
            </FormGrid>
          </FormShell>
        </Panel>
      )}
    </Wrapper>
  )
}

export default SellerFilterButton

const Wrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`

const Trigger = styled.button<{ $active: boolean; $open: boolean }>`
  position: relative;
  box-sizing: border-box;
  width: 2.5rem;
  height: 2.5rem;
  background: ${({ theme, $active }) => ($active ? theme.colors.accentSoft : theme.colors.surface)};
  border: 1px solid
    ${({ theme, $active, $open }) => ($open || $active ? theme.colors.ink : theme.colors.inputBorder)};
  border-radius: 0.875rem;
  color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.icon)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const Count = styled.span`
  position: absolute;
  top: -0.375rem;
  inset-inline-start: -0.375rem;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.ink};
  border-radius: 999px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
`

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  inset-inline-end: 0;
  z-index: 30;
  width: 20rem;
  max-width: calc(100vw - 1.5rem);
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 1rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 0.875rem;
  direction: rtl;
`
